import { createReducer, on } from "@ngrx/store";
import { ReducerUtilsService } from "../../../services/reducer-utils.service";
import { VigilantApiDtosOrgsGetResponse } from "./orgs.model";

import * as OrgsActions from "./orgs.actions";
export { OrgsActions };

/**
 * Represents the state for Channel.
 *
 * @typedef {Object} OrgsState
 * @property {VigilantApiDtosOrgsGetResponse[] | undefined} orgs - An array of Channel objects or undefined.
 */
export interface OrgsState {
  orgs: VigilantApiDtosOrgsGetResponse[] | undefined;
}

/**
 * The default state for Channel.
 *
 * @type {OrgsState}
 */
const defaultState: OrgsState = {
  orgs: undefined,
};

/**
 * Reducer function to handle Channels state changes.
 *
 * This reducer manages state transitions for Channels through various actions:
 *
 * - **SetOrgs**: Sets the Channel state to the provided array.
 * - **ResetOrgs**: Resets the Channel state using the default reset logic.
 * - **AddOrg**: Adds a single Channel to the current Channels state.
 * - **AddOrgs**: Adds multiple Channel to the current Channels state.
 * - **UpdateOrg**: Updates a single Channel in the Channels state.
 * - **UpdateOrgs**: Updates multiple Channel in the Channels state.
 * - **DeleteOrg**: Removes a single Channel by its identifier.
 * - **DeleteOrgs**: Removes multiple Channel by their identifiers.
 *
 * @constant
 * @function orgsReducer
 * @param {OrgsState} state - The current state.
 * @param {Object} action - The dispatched action containing the necessary payload.
 * @returns {OrgsState} The updated state.
 */
export const orgsReducer = createReducer(
  defaultState,

  /**
   * Handles setting the Channels.
   *
   * @param {OrgsState} state - The current Channels state.
   * @param {Object} action - The action containing the Channels payload.
   * @returns {OrgsState} The new state with Channels set.
   */
  on(
    OrgsActions.SetOrgs,
    (state, action): OrgsState => ({
      ...state,
      orgs: ReducerUtilsService.set(action.orgs),
    })
  ),

  /**
   * Handles resetting the Channels.
   *
   * @param {OrgsState} state - The current Channels state.
   * @returns {OrgsState} The new state with Channels reset.
   */
  on(
    OrgsActions.ResetOrgs,
    (state): OrgsState => ({
      ...state,
      orgs: ReducerUtilsService.reset(),
    })
  ),

  /**
   * Handles adding a single Channel.
   *
   * @param {OrgsState} state - The current Channels state.
   * @param {Object} action - The action containing the Channel to add.
   * @returns {OrgsState} The new state with the Channel added.
   */
  on(
    OrgsActions.AddOrg,
    (state, action): OrgsState => ({
      ...state,
      orgs: ReducerUtilsService.addItem(state.orgs, action.org),
    })
  ),

  /**
   * Handles adding multiple Channels.
   *
   * @param {OrgsState} state - The current Channels state.
   * @param {Object} action - The action containing the Channels to add.
   * @returns {OrgsState} The new state with the Channels added.
   */
  on(
    OrgsActions.AddOrgs,
    (state, action): OrgsState => ({
      ...state,
      orgs: ReducerUtilsService.addItems(state.orgs, action.orgs),
    })
  ),

  /**
   * Handles updating a single Channel.
   *
   * @param {OrgsState} state - The current Channels state.
   * @param {Object} action - The action containing the updated Channel.
   * @returns {OrgsState} The new state with the Channel updated.
   */
  on(
    OrgsActions.UpdateOrg,
    (state, action): OrgsState => ({
      ...state,
      orgs: ReducerUtilsService.updateItem(state.orgs, action.org),
    })
  ),

  /**
   * Handles updating multiple Channels.
   *
   * @param {OrgsState} state - The current organizaChannelstions state.
   * @param {Object} action - The action containing the Channels to update.
   * @returns {OrgsState} The new state with the Channels updated.
   */
  on(
    OrgsActions.UpdateOrgs,
    (state, action): OrgsState => ({
      ...state,
      orgs: ReducerUtilsService.updateItems(state.orgs, action.orgs),
    })
  ),

  /**
   * Handles deleting a single Channel.
   *
   * @param {OrgsState} state - The current Channels state.
   * @param {Object} action - The action containing the id of the Channel to delete.
   * @returns {OrgsState} The new state with the Channel removed.
   */
  on(
    OrgsActions.DeleteOrg,
    (state, action): OrgsState => ({
      ...state,
      orgs: ReducerUtilsService.deleteItem(state.orgs, action.id),
    })
  ),

  /**
   * Handles deleting multiple Channels.
   *
   * @param {OrgsState} state - The current Channels state.
   * @param {Object} action - The action containing the ids of Channels to delete.
   * @returns {OrgsState} The new state with the specified Channels removed.
   */
  on(
    OrgsActions.DeleteOrgs,
    (state, action): OrgsState => ({
      ...state,
      orgs: ReducerUtilsService.deleteItems(state.orgs, action.ids),
    })
  )
);
